import {
  useQuery,
  useMutation,
  useQueryClient,
  queryOptions,
  type QueryClient,
  type QueryKey,
  type EnsureQueryDataOptions,
  type EnsureInfiniteQueryDataOptions,
  type UseQueryOptions,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { apiClient } from '@repo/shared/client';
import type { ApiContract, InferResponse, ApiRequestOptions, Segment } from '@repo/shared/common';
import { modelingCurvesContract, modelingStatusContract, isEmptySegment } from '@repo/shared/common';
import { createContractFetcher } from './loader-fetch';

/**
 * Builds TanStack Query `queryOptions` for a GET contract, backed by the
 * isomorphic fetcher ({@link createContractFetcher}). This is the single
 * definition a route **loader** (`queryClient.ensureQueryData` /
 * `prefetchQuery`) and the **component** (`useQuery`) both consume — so loader
 * prefetch, SSR hydration, and component reads all share one cache entry with
 * no waterfall and no double-fetch.
 *
 * Wrap it per feature (e.g. `currentOrganizationQueryOptions()`) so call sites
 * pass typed args, not raw contracts + keys.
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type -- TanStack's `queryOptions` returns an intentionally-opaque branded type; spelling it out is fragile and discouraged. The contract generic keeps callers fully typed.
export function contractQueryOptions<
  T extends ApiContract & { readonly method: 'GET' },
>(contract: T, queryKey: readonly unknown[], options?: ApiRequestOptions<T>) {
  const fetcher = createContractFetcher(contract);
  return queryOptions<InferResponse<T>>({
    queryKey,
    queryFn: () => fetcher(options),
  });
}

/** Root key — every modeling query nests under it for blanket invalidation. */
export const MODELING_KEY = ['modeling'] as const;

/** Encode a segment as the base64url(JSON) the modeling read carries — IDENTICAL to
 *  the Portfolio loans query encoding, so the same store drives both screens and the
 *  cohort cannot drift between them. utf8-safe (the segment may carry non-ASCII labels). */
function encodeSegment(segment: Segment): string {
  const bytes = new TextEncoder().encode(JSON.stringify(segment));
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * `queryOptions` for the Modeling cohort-analytics read, shared by the route loader
 * and the component (one cache entry, no waterfall). Built on {@link contractQueryOptions}.
 *
 * The active segment rides the query string (encoded like the Portfolio read) and is
 * keyed into the cache, so changing the slice is a new cache entry. Pass the
 * **debounced** segment from the caller (mirroring Portfolio) so dragging a range
 * slider does not fire a read per tick. An empty segment means the whole portfolio:
 * we omit the param so the empty-segment read shares a cache entry regardless of how
 * the empty state was reached.
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type -- opaque queryOptions brand; the contract keeps callers typed.
export function modelingCurvesQueryOptions(segment?: Segment) {
  const isEmpty = segment === undefined || isEmptySegment(segment);
  const query = isEmpty ? {} : { segment: encodeSegment(segment) };
  // Key on the encoded segment (stable + serializable) so the cache splits per slice.
  const key = [...MODELING_KEY, 'curves', isEmpty ? null : query.segment] as const;
  return contractQueryOptions(modelingCurvesContract, key, { query });
}

/** Query key for the block-until-settled status poll. Shared by the Modeling + Portfolio screens
 *  (both gate on the same org-level freshness), so it nests under the modeling root for one cache
 *  entry across the two. */
export const MODELING_STATUS_KEY = [...MODELING_KEY, 'status'] as const;

/**
 * `queryOptions` for the cheap block-until-settled status poll (`GET /api/modeling/status`), shared
 * by the route loader (warm the block/unblock decision flash-free) and the `useModelingStatus`
 * hook. No segment: the freshness is a whole-org property, so one cache entry serves both screens.
 *
 * Freshness is SELF-HEALING across navigation: `staleTime: 0` + `refetchOnMount: 'always'` override
 * the global 5-min staleTime, so every mount (a route change onto Modeling or Portfolio) revalidates
 * the block/unblock decision instead of trusting a cached `fresh` that a background upload/delete may
 * have since invalidated. The poll (`useModelingStatus`) then keeps it live while not fresh.
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type -- opaque queryOptions brand; the contract keeps callers typed.
export function modelingStatusQueryOptions() {
  return {
    ...contractQueryOptions(modelingStatusContract, [...MODELING_STATUS_KEY]),
    staleTime: 0,
    refetchOnMount: 'always' as const,
  };
}

/**
 * Loader helper for **page-defining** data: await on the server (so the data
 * ships in the SSR HTML — no first-paint flash) but only kick off a prefetch on
 * the client (so a client navigation stays snappy and the component shows a
 * skeleton while it loads). `defaultPreload: 'intent'` starts the client prefetch
 * on hover, so the skeleton is usually brief.
 *
 * For **secondary** / below-the-fold data, skip this and call
 * `queryClient.prefetchQuery(...)` directly (don't block SSR on it). For very
 * large payloads, stream instead (see the data-loading guidance). The await/skip
 * decision is per route — this helper just encodes the common "await on server"
 * case in one place.
 */
export async function ensureRouteData<
  TQueryFnData = unknown,
  TError = Error,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  queryClient: QueryClient,
  options: EnsureQueryDataOptions<TQueryFnData, TError, TData, TQueryKey>,
): Promise<void> {
  if (import.meta.env.SSR) {
    await queryClient.ensureQueryData(options);
  } else {
    // EnsureQueryDataOptions extends FetchQueryOptions, so it's a valid prefetch input.
    void queryClient.prefetchQuery(options);
  }
}

/** {@link ensureRouteData} for an infinite (cursor-paginated) query. */
export async function ensureRouteInfiniteData<
  TQueryFnData = unknown,
  TError = Error,
  TData = unknown,
  TQueryKey extends QueryKey = QueryKey,
  TPageParam = unknown,
>(
  queryClient: QueryClient,
  options: EnsureInfiniteQueryDataOptions<
    TQueryFnData,
    TError,
    TData,
    TQueryKey,
    TPageParam
  >,
): Promise<void> {
  if (import.meta.env.SSR) {
    await queryClient.ensureInfiniteQueryData(options);
  } else {
    void queryClient.prefetchInfiniteQuery(options);
  }
}

/**
 * Creates a React hook that fetches data for a GET contract via TanStack Query.
 * The query key, fetch function, and return type are all derived from the contract.
 *
 * @param contract - An API contract with method 'GET'.
 * @param queryKey - The base query key for caching/invalidation.
 */
export function createQueryHook<T extends ApiContract & { readonly method: 'GET' }>(
  contract: T,
  queryKey: readonly string[],
) {
  return function useContractQuery(
    options?: Omit<
      UseQueryOptions<InferResponse<T>>,
      'queryKey' | 'queryFn'
    > & { requestOptions?: ApiRequestOptions<T> },
  ) {
    const { requestOptions, ...queryOptions } = options ?? {};

    return useQuery<InferResponse<T>>({
      queryKey: [...queryKey],
      queryFn: () => apiClient(contract, requestOptions),
      ...queryOptions,
    });
  };
}

/**
 * Creates a React hook that sends a mutation request for a contract via TanStack Query.
 * After a successful mutation, callers should invalidate related queries.
 *
 * @param contract - An API contract (POST, PUT, PATCH, DELETE).
 * @param invalidateKeys - Query keys to invalidate on success.
 */
export function createMutationHook<T extends ApiContract>(
  contract: T,
  invalidateKeys?: readonly (readonly string[])[],
) {
  return function useContractMutation(
    options?: Omit<
      UseMutationOptions<InferResponse<T>, Error, ApiRequestOptions<T>>,
      'mutationFn'
    >,
  ) {
    const queryClient = useQueryClient();
    const { onSuccess: callerOnSuccess, ...restOptions } = options ?? {};

    return useMutation<InferResponse<T>, Error, ApiRequestOptions<T>>({
      ...restOptions,
      mutationFn: (requestOptions) => apiClient(contract, requestOptions),
      async onSuccess(...args) {
        if (invalidateKeys) {
          await Promise.all(
            invalidateKeys.map((key) =>
              queryClient.invalidateQueries({ queryKey: [...key] }),
            ),
          );
        }
        callerOnSuccess?.(...args);
      },
    });
  };
}
